import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Lock component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconLock extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12 1.10516C10.3523 1.10516 8.77211 1.75971 7.60701 2.92482C6.4419 4.08992 5.78735 5.67015 5.78735 7.31786V8.90887H5.75706C4.48086 8.90887 3.44629 9.94344 3.44629 11.2196V20.5843C3.44629 21.8605 4.48086 22.895 5.75706 22.895H18.2432C19.5194 22.895 20.554 21.8605 20.554 20.5843V11.2196C20.554 9.94344 19.5194 8.90887 18.2432 8.90887H18.2127V7.31786C18.2127 5.67015 17.5582 4.08992 16.3931 2.92482C15.228 1.75971 13.6478 1.10516 12 1.10516ZM17.4484 10.4089C17.4532 10.409 17.4579 10.409 17.4627 10.409C17.4675 10.409 17.4723 10.409 17.4771 10.4089H18.2432C18.691 10.4089 19.054 10.7719 19.054 11.2196V20.5843C19.054 21.032 18.691 21.395 18.2432 21.395H5.75706C5.30928 21.395 4.94629 21.032 4.94629 20.5843V11.2196C4.94629 10.7719 5.30928 10.4089 5.75706 10.4089H6.52301C6.52778 10.409 6.53256 10.409 6.53735 10.409C6.54215 10.409 6.54693 10.409 6.5517 10.4089H17.4484ZM16.7127 8.90887V7.31786C16.7127 6.06797 16.2162 4.86928 15.3324 3.98548C14.4486 3.10168 13.2499 2.60516 12 2.60516C10.7502 2.60516 9.55147 3.10168 8.66767 3.98548C7.78387 4.86928 7.28735 6.06797 7.28735 7.31786V8.90887H16.7127Z"
          />
          <path
            d="M12.0001 16.6825C12.4311 16.6825 12.7805 16.3331 12.7805 15.9021C12.7805 15.4711 12.4311 15.1217 12.0001 15.1217C11.5691 15.1217 11.2197 15.4711 11.2197 15.9021C11.2197 16.3331 11.5691 16.6825 12.0001 16.6825Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.0001 15.8717C11.9833 15.8717 11.9697 15.8853 11.9697 15.9021C11.9697 15.9189 11.9833 15.9325 12.0001 15.9325C12.0169 15.9325 12.0305 15.9189 12.0305 15.9021C12.0305 15.8853 12.0169 15.8717 12.0001 15.8717ZM10.4697 15.9021C10.4697 15.0569 11.1549 14.3717 12.0001 14.3717C12.8453 14.3717 13.5305 15.0569 13.5305 15.9021C13.5305 16.7473 12.8453 17.4325 12.0001 17.4325C11.1549 17.4325 10.4697 16.7473 10.4697 15.9021Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-lock') === undefined) {
  customElements.define('cat-icon-lock', BlocksIconLock);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-lock': BlocksIconLock;
  }
}
